import _clone from 'lodash/clone';
import _pick from 'lodash/pick';
import Immutable from 'seamless-immutable';
import RefData from '../../ref-data';

import { getAssetRefWithFallback, replaceIntegrationGenericFields, getIntegrationGenericAssetRef } from './utils';

/**
 * IntegrationTemplate
 * @typedef {object} IntegrationTemplate
 * @property {object} blueprint - describes the fields required to configure the integration
 * @property {string} classRef - class of integration
 * @property {object} config -
 * @property {object} definition -
 * @property {string} description -
 * @property {RefData} eventType - the event that triggers the integration
 * @property {string} help - help text to assist users in filling in the integration config field
 * @property {string} icon - path to the icon to use for the integration in the list
 * @property {number} id
 * @property {string} name - the name of the integration
 * @property {bit} singleInstance - defines whether you can configure more than one of this type of template
 * @property {RefData} status - current status of the template
 * @property {RefData} typeRef - type of the template
 */

const TEMPLATE_PICKER_SIZE = 250;

/**
 * Create a IntegrationTemplateModel from provided data
 */

function create(integrationTemplate = {}) {
  const result = _clone(integrationTemplate);

  result.classRef = RefData.Model.hydrate(integrationTemplate.classRef);
  result.eventType = RefData.Model.hydrate(integrationTemplate.eventType);
  result.status = RefData.Model.hydrate(integrationTemplate.status);
  result.typeRef = RefData.Model.hydrate(integrationTemplate.typeRef);

  // we do this here rather than in the model as we want to avoid having the 'icon' param on the model and only use assetRef
  result.iconPath = result.icon;
  delete result.icon;

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend integrationTemplate object into a frontend IntegrationTemplateModel
 * @param {Object} integrationTemplate - backend formatted integrationTemplate
 * @returns {IntegrationTemplate}
 */
function deserialize(integrationTemplate) {
  let result = _pick(integrationTemplate, _keys);

  // Hot data replacement for some kinds of integrations
  result = replaceIntegrationGenericFields(result, true);

  return create(result);
}

/**
 * Transform a IntegrationTemplateModel into a backend integrationTemplate object
 * @returns {Object}
 */
function serialize(integrationTemplate) {
  const result = integrationTemplate.asMutable({ deep: true });

  result.classRef = integrationTemplate.classRef.id;
  result.eventType = integrationTemplate.eventType.id;
  result.status = integrationTemplate.status.id;
  result.typeRef = integrationTemplate.typeRef.id;

  result.icon = result.iconPath;
  delete result.iconPath;

  return _pick(result, _keys);
}

/** The IntegrationTemplateModel prototype */
const model = {
  get isDisabled() {
    return this.status.id === RefData.Enum.STATUS.INTEGRATIONTEMPLATE.USED;
  },
  get header() {
    // Campaign Monitor -> Agency accounts generic product name
    if (this.typeRef.id === RefData.Enum.TYPE.INTEGRATION.CAMPAIGNMONITOR) {
      const { accountType, productName } = App.globalContext.account.providerProperties;
      if (accountType && accountType === RefData.Enum.TYPE.CAMPAIGN_MONITOR.AGENCY) {
        return productName;
      }
    }
    return this.name;
  },
  get byline() {
    return this.isDisabled ? 'Status: already in use' : 'Status: available';
  },
  get assetRef() {
    return getIntegrationGenericAssetRef(
      this.typeRef.id,
      getAssetRefWithFallback({
        name:             this.name,
        icon:             this.iconPath,
        fallbackFontSize: '172px',
        height:           TEMPLATE_PICKER_SIZE,
        width:            TEMPLATE_PICKER_SIZE,
      })
    );
  },

  // set up a standard side for the asset box image, because it can be generic now
  get height() {
    return TEMPLATE_PICKER_SIZE;
  },

  get width() {
    return TEMPLATE_PICKER_SIZE;
  },

  get value() {
    return this.id;
  },

  get tooltip() {
    return this.status.id === RefData.Enum.STATUS.INTEGRATIONTEMPLATE.USED
      ? 'You can only have one integration of this type selected.'
      : undefined;
  },
};

/**
 * An array of keys on the API IntegrationTemplate object that should be shared between the API requests and responses
 */
const _keys = [
  'blueprint',
  'classRef',
  'config',
  'definition',
  'description',
  'eventType',
  'help',
  'icon',
  'id',
  'name',
  'singleInstance',
  'status',
  'typeRef',
];

export default {
  create,
  deserialize,
  serialize,
};
